import { IDiagnosisCode } from "../types";

export interface DiagnosisCodeOption {
    label: string;
    value: string;
    isDeprecated?: boolean;
}

const formatDiagnosisLabel = (diagnosis: IDiagnosisCode): string =>
    `${diagnosis.code} ${diagnosis.description}`;

export const filterDiagnosisCodesBySearchTerm = (
    diagnosisCodes?: IDiagnosisCode[],
    searchTerm?: string
): IDiagnosisCode[] => {
    const normalizedSearchTerm = searchTerm?.trim().toLowerCase();
    if (!normalizedSearchTerm) return diagnosisCodes ?? [];
    return (diagnosisCodes ?? []).filter(
        (diagnosis) =>
            diagnosis.code.toLowerCase().includes(normalizedSearchTerm) ||
            diagnosis.description.toLowerCase().includes(normalizedSearchTerm)
    );
};

export const getActiveDiagnosisCodeOptions = (
    diagnosisCodes?: IDiagnosisCode[]
): DiagnosisCodeOption[] =>
    (diagnosisCodes ?? [])
        .filter((diagnosis) => !diagnosis.deprecated)
        .map((diagnosis) => ({
            label: formatDiagnosisLabel(diagnosis),
            value: diagnosis.code,
        }));

export const getDiagnosisDefaultValue = (
    code?: string | null,
    diagnosisCodes?: IDiagnosisCode[],
    preserveMissing = false
) => {
    if (!code) {
        return null;
    }

    const match = diagnosisCodes?.find((diagnosis) => diagnosis.code === code);

    if (!match) {
        if (!preserveMissing) return null;
        return {
            label: code,
            value: code,
        };
    }

    return {
        label: match.deprecated
            ? `${formatDiagnosisLabel(match)} (Deprecated)`
            : formatDiagnosisLabel(match),
        value: match.code,
    };
};

export const getDiagnosisDefaultValues = (
    codes?: string[] | null,
    diagnosisCodes?: IDiagnosisCode[],
    preserveMissing = false
) => {
    if (!codes?.length) {
        return null;
    }

    return codes
        .map((code) => {
            const match = diagnosisCodes?.find(
                (diagnosis) => diagnosis.code === code
            );
            if (!match) {
                if (!preserveMissing) return null;
                return {
                    label: code,
                    value: code,
                };
            }
            return {
                label: match.deprecated
                    ? `${formatDiagnosisLabel(match)} (Deprecated)`
                    : formatDiagnosisLabel(match),
                value: match.code,
            };
        })
        .filter((option): option is DiagnosisCodeOption => option !== null);
};
